import type { CharacterBuild, CharacterStyle } from './characterStyle';
import { createPose, createSkeleton, Joint, POSE_HIPS_OFFSET, POSE_SIZE, type Pose, type Skeleton } from './skeleton';

/**
 * Pure, deterministic character animation state machine. Feed it one AnimInput per rendered frame;
 * it returns a joint-rotation Pose (see skeleton.ts) that the rig — or an instanced renderer via
 * writeJointMatrices — applies. No three.js, no allocation per update (all buffers are owned).
 *
 * - Locomotion phase is driven by distance travelled over a speed-dependent stride, and the stance
 *   foot sweeps back at exactly ground speed, so feet plant instead of skating.
 * - State changes cross-fade over BLEND_TIME (smoothstep weights, from + to = 1). If a new change
 *   interrupts a running blend, the current blended pose is frozen as the source: never a pop.
 * - Secondary motion (coat-tail spring), turn lead/lean and head stabilisation are layered on top.
 * - Footsteps fire alternately (left at phase 0, right at 0.5) through `onFootstep`.
 */

export type AnimState =
  | 'idle'
  | 'walk'
  | 'run'
  | 'sprint'
  | 'start'
  | 'stop'
  | 'turn'
  | 'enterVehicle'
  | 'exitVehicle'
  | 'stealVehicle'
  | 'knockdown'
  | 'getUp'
  | 'busted'
  | 'wasted'
  | 'cower';

/** What the character is doing beyond moving. 'none' = free locomotion. */
export type AnimAction = 'none' | 'enterVehicle' | 'exitVehicle' | 'stealVehicle' | 'knockdown' | 'busted' | 'wasted' | 'cower';

/**
 * How a carried weapon is held (upper-body layer, see applyWeaponLayer). 'none' = empty hands
 * (no layer); 'fist' only shows when raised (a boxer's guard) or punching.
 */
export type WeaponHold = 'none' | 'fist' | 'club' | 'blade' | 'pistol' | 'shotgun';
/** The weapon move being played: a melee swing, a shot's recoil or a reload. */
export type WeaponMove = 'none' | 'swing' | 'fire' | 'reload';

/** Upper-body weapon layer input. Legs keep walking underneath; the arms (and chest twist) follow this. */
export interface WeaponAnim {
  hold: WeaponHold;
  move: WeaponMove;
  /** 0..1 progress through `move` (swing, recoil, reload). */
  t: number;
  /** 0..1 how far the weapon is raised (aiming / guard). */
  raise: number;
}

export interface AnimInput {
  /** Frame time (s). */
  dt: number;
  /** Ground speed (m/s, world units). */
  speed: number;
  /** Run button held (sprint gait at speed). */
  running: boolean;
  /** Heading change rate (rad/s, positive = turning left). */
  turnRate: number;
  action: AnimAction;
  /** 0..1 progress of enter/exit/steal actions. */
  actionProgress: number;
  /** Optional world heading (rad) — only used to make footstep offsets world-relative. */
  heading?: number;
  /** Optional weapon layer (carry pose, aim, swing, recoil, reload). */
  weapon?: WeaponAnim | null;
  /**
   * Optional head look-at: yaw of the target relative to the body heading (rad, + = to the left) and
   * how strongly to look (0..1). The head and neck turn (the chest helps past ~45°), smoothed.
   */
  lookYaw?: number;
  lookWeight?: number;
  /** 0..1 standing in a group: idles become chatting (gestures while talking, nods while listening). */
  social?: number;
  /** A blow or a bullet just landed: flinch (light) or stagger (strength > 0.5). */
  hit?: HitReaction | null;
}

/** Hit reaction input: seconds since the hit, where it came from (rad, body-relative, 0 = in front), 0..1 strength. */
export interface HitReaction {
  t: number;
  dir: number;
  strength: number;
}

/** Per-character habits that shape gait, posture and idles. */
export interface AnimTraits {
  /** 0 young … 1 pensioner: stoop, shorter arm swing, hands behind the back. */
  age: number;
  /** Women's carriage: narrower arm swing, hip sway. */
  female: boolean;
  /** Idles with a smoke. */
  smoker: boolean;
  /**
   * 0..1 swagger (third pass): a rolling walk with the shoulders, elbows out, a longer stride, chest
   * up and the weight back when standing; mods, skinheads, rockers, dockers and the villain.
   */
  swagger: number;
  /** 0..1 bulk (from the build): feet apart, more bob, arms carried away from the body, a slower cadence. */
  bulk: number;
}

/** Idle habits the animator cycles through when a character stands about. */
export type IdleAct = 'none' | 'watch' | 'smoke' | 'pockets' | 'fold' | 'scratch' | 'behind' | 'talk' | 'listen' | 'winded' | 'hips' | 'yawn' | 'collar';

export interface FootstepEvent {
  foot: 'left' | 'right';
  /** World-relative offset (m) of the planted foot from the character root (uses heading + scale). */
  dx: number;
  dz: number;
  /** Ground speed at the plant (m/s). */
  speed: number;
  /** 0 = gentle walk … 1 = flat-out sprint. */
  intensity: number;
}

export interface AnimatorOptions {
  build?: CharacterBuild;
  height?: number;
  /** Visual scale the rig is drawn at (stride maths must match the on-screen leg length). */
  scale?: number;
  /** Seed for idle variation (weight shifts, glances). */
  seed?: number;
  /** Authored player proportions (longer legs for the stride maths); see skeleton.ts. */
  sculpt?: CharacterStyle['sculpt'];
}

export interface CharacterAnimator {
  readonly skeleton: Skeleton;
  readonly state: AnimState;
  readonly previousState: AnimState;
  /** Seconds in the current state. */
  readonly stateTime: number;
  /** Locomotion cycle position [0, 1): 0 = left heel strike, 0.5 = right. */
  readonly phase: number;
  /** Cross-fade weights; from + to = 1. `to` is the current state's weight. */
  readonly weights: { readonly from: number; readonly to: number };
  /** The output pose (same object every frame). */
  readonly pose: Pose;
  /** Footsteps emitted during the last update. */
  readonly stepsThisFrame: number;
  /** Called for each planted foot. The event object is reused — copy what you keep. */
  onFootstep: ((e: FootstepEvent) => void) | null;
  /** Current idle habit (for tests and views that show a prop, e.g. the cigarette). */
  readonly idleAct: IdleAct;
  readonly traits: Readonly<AnimTraits>;
  /** Melee combo position of the current / last swing (0, 1, 2 …). */
  readonly combo: number;
  setTraits(t: Partial<AnimTraits>): void;
  update(input: AnimInput): Pose;
  /** Snap to a state with no blend (respawn, teleport). */
  reset(state?: AnimState): void;
}

/** Cross-fade (s) between two gaits (walk ↔ run ↔ sprint): longer than a state's own blend, never a pop. */
export const GAIT_BLEND_TIME = 0.32;
const GAITS: ReadonlySet<AnimState> = new Set<AnimState>(['walk', 'run', 'sprint']);

export const BLEND_TIME: Readonly<Record<AnimState, number>> = {
  idle: 0.25,
  walk: 0.2,
  run: 0.2,
  sprint: 0.22,
  start: 0.15,
  stop: 0.2,
  turn: 0.2,
  enterVehicle: 0.2,
  exitVehicle: 0.15,
  stealVehicle: 0.2,
  knockdown: 0.15,
  getUp: 0.2,
  busted: 0.3,
  wasted: 0.2,
  cower: 0.18,
};
export const START_TIME = 0.22;
export const STOP_TIME = 0.35;
export const GETUP_TIME = 1.1;

const TAU = Math.PI * 2;
const LOCO: ReadonlySet<AnimState> = new Set<AnimState>(['walk', 'run', 'sprint', 'start']);
const FOOTED: ReadonlySet<AnimState> = new Set<AnimState>(['walk', 'run', 'sprint', 'start', 'stop', 'enterVehicle', 'exitVehicle', 'stealVehicle']);
/** States whose own arm work wins over the weapon layer (falls, arrests, getting in and out of cars). */
const NO_WEAPON_LAYER: ReadonlySet<AnimState> = new Set<AnimState>(['enterVehicle', 'exitVehicle', 'stealVehicle', 'knockdown', 'getUp', 'busted', 'wasted', 'cower']);
/** States that ignore hit reactions (already on the floor or going down) and the head look-at. */
const NO_REACT: ReadonlySet<AnimState> = new Set<AnimState>(['knockdown', 'getUp', 'wasted', 'busted']);
const NO_LOOK: ReadonlySet<AnimState> = new Set<AnimState>(['knockdown', 'getUp', 'wasted', 'enterVehicle', 'exitVehicle', 'stealVehicle', 'cower']);

const clamp01 = (v: number): number => (v < 0 ? 0 : v > 1 ? 1 : v);
const clamp = (v: number, lo: number, hi: number): number => (v < lo ? lo : v > hi ? hi : v);
const smooth = (t: number): number => {
  const c = clamp01(t);
  return c * c * (3 - 2 * c);
};
const band = (a: number, b: number, t: number): number => smooth((t - a) / (b - a));
const damp = (rate: number, dt: number): number => 1 - Math.exp(-rate * dt);

/** Distance (m, world) covered by one full two-step cycle at a given speed. */
export function cycleLength(speed: number, height = 1): number {
  return height * clamp(0.95 + 0.33 * speed, 1.15, 3.4);
}

/** Walk / run / sprint classification with hysteresis around the current state. */
export function gaitFor(speed: number, running: boolean, current: AnimState): 'walk' | 'run' | 'sprint' {
  const sprintOn = current === 'sprint' ? 4.6 : 5.2;
  if (running && speed >= sprintOn) return 'sprint';
  const runOn = current === 'run' || current === 'sprint' ? 3.0 : 3.5;
  if (speed >= runOn) return 'run';
  return 'walk';
}

// --- weapon layer ----------------------------------------------------------------------------------

const JW = Joint;
const wToward = (p: Pose, j: number, axis: number, v: number, w: number): void => {
  const i = j * 3 + axis;
  p[i] = p[i]! + (v - p[i]!) * w;
};
const wAdd = (p: Pose, j: number, axis: number, v: number): void => {
  p[j * 3 + axis] = p[j * 3 + axis]! + v;
};
/** [joint, x, y, z] arm targets. Right arm z > 0 / left arm z < 0 = across the chest. */
type ArmKey = readonly (readonly [number, number, number, number])[];
function pull(p: Pose, keys: ArmKey, w: number): void {
  if (w <= 0) return;
  for (const [j, x, y, z] of keys) {
    wToward(p, j, 0, x, w);
    wToward(p, j, 1, y, w);
    wToward(p, j, 2, z, w);
  }
}

/** Arms when the weapon is carried (lowered). */
const CARRY: Record<WeaponHold, ArmKey> = {
  none: [],
  fist: [],
  // Club hangs from the fist, angled forward so it reads from above.
  club: [
    [JW.upperArmR, -0.28, 0, -0.16],
    [JW.lowerArmR, -0.55, 0, 0],
  ],
  blade: [
    [JW.upperArmR, -0.2, 0, -0.12],
    [JW.lowerArmR, -0.75, 0, 0],
  ],
  pistol: [
    [JW.upperArmR, -0.12, 0, -0.1],
    [JW.lowerArmR, -0.4, 0, 0],
  ],
  // Low ready, both hands on the gun across the body.
  shotgun: [
    [JW.upperArmR, -0.35, 0, 0.12],
    [JW.lowerArmR, -0.95, 0, 0],
    [JW.upperArmL, -0.75, 0, -0.32],
    [JW.lowerArmL, -1.05, 0, 0],
  ],
};

/** Arms when raised: aiming a gun, or a guard with melee weapons. */
const RAISED: Record<WeaponHold, ArmKey> = {
  none: [],
  // Boxer's guard.
  fist: [
    [JW.upperArmR, -0.55, 0, 0.3],
    [JW.lowerArmR, -1.95, 0, 0],
    [JW.upperArmL, -0.65, 0, -0.32],
    [JW.lowerArmL, -1.9, 0, 0],
  ],
  club: [
    [JW.upperArmR, -0.9, 0, -0.35],
    [JW.lowerArmR, -1.25, 0, 0],
    [JW.upperArmL, -0.6, 0, -0.3],
    [JW.lowerArmL, -1.5, 0, 0],
  ],
  blade: [
    [JW.upperArmR, -0.85, 0, 0.05],
    [JW.lowerArmR, -0.7, 0, 0],
    [JW.upperArmL, -0.55, 0, -0.25],
    [JW.lowerArmL, -1.4, 0, 0],
  ],
  // Two-handed, arms out straight at shoulder height, hands meeting on the centre line.
  pistol: [
    [JW.upperArmR, -1.5, 0, 0.3],
    [JW.lowerArmR, -0.06, 0, 0],
    [JW.upperArmL, -1.42, 0, -0.52],
    [JW.lowerArmL, -0.22, 0, 0],
  ],
  // Held level at the chest (a sawn-off isn't shouldered): trigger hand at the breastbone, the
  // other hand out under the barrels. Upper + lower arm pitch ~ -pi/2 keeps the barrels level.
  shotgun: [
    [JW.upperArmR, -1.05, 0, 0.3],
    [JW.lowerArmR, -0.5, 0, 0],
    [JW.upperArmL, -1.32, 0, -0.34],
    [JW.lowerArmL, -0.26, 0, 0],
  ],
};

const RELOAD: Record<'pistol' | 'shotgun', ArmKey> = {
  // Gun brought in to the chest, the other hand thumbing rounds into the cylinder. The wrist folds
  // the muzzle down to about level (the bent forearm alone would hold it pointing at the sky).
  pistol: [
    [JW.upperArmR, -0.55, 0, 0.38],
    [JW.lowerArmR, -1.7, 0, 0],
    [JW.handR, 0.75, 0, 0],
    [JW.upperArmL, -0.62, 0, -0.38],
    [JW.lowerArmL, -1.55, 0, 0],
  ],
  // Broken open over the forearm, shells pushed into the breech.
  shotgun: [
    [JW.upperArmR, -0.25, 0, 0.1],
    [JW.lowerArmR, -1.25, 0, 0],
    [JW.upperArmL, -0.7, 0, -0.42],
    [JW.lowerArmL, -1.35, 0, 0],
  ],
};

const bandW = (a: number, b: number, t: number): number => {
  const c = Math.max(0, Math.min(1, (t - a) / (b - a)));
  return c * c * (3 - 2 * c);
};

/** Extra, stateful context for the weapon layer that the animator tracks (all optional). */
export interface WeaponLayerContext {
  /** Combo position of this swing: punches go jab, cross, hook; clubs forehand, backhand, chop. */
  combo?: number;
  /** 0..1 how still the legs are (a melee strike lunges onto the front foot when standing). */
  still?: number;
  /** 0..1 progress of drawing the weapon from the coat (1 = done / not drawing). */
  draw?: number;
}

/**
 * Layer a weapon pose over an animator pose (in place). Pure: tests and instanced renderers (armed
 * police) use it directly. `height` is the skeleton height (hips offsets are metres).
 */
export function applyWeaponLayer(p: Pose, w: WeaponAnim, height: number, ctx?: WeaponLayerContext): void {
  const hold = w.hold;
  if (hold === 'none') return;
  const raise = Math.max(0, Math.min(1, w.raise));
  const t = Math.max(0, Math.min(1, w.t));
  pull(p, CARRY[hold], 1 - raise);
  pull(p, RAISED[hold], raise);
  const still = ctx?.still ?? 0;
  if (raise > 0) {
    // Bladed stance: lead shoulder forward, head level over the sights.
    const gun = hold === 'pistol' || hold === 'shotgun';
    wAdd(p, JW.chest, 1, (hold === 'shotgun' ? 0.28 : gun ? 0.08 : 0.15) * raise);
    wToward(p, JW.head, 1, (hold === 'shotgun' ? -0.25 : gun ? -0.05 : -0.1) * raise, raise);
    wToward(p, JW.head, 0, 0, raise * 0.6);
    // Standing: feet apart, knees soft, weight forward.
    const k = raise * still;
    if (k > 0) {
      wAdd(p, JW.upperLegL, 0, -0.18 * k);
      wAdd(p, JW.lowerLegL, 0, 0.16 * k);
      wAdd(p, JW.upperLegR, 0, 0.14 * k);
      wAdd(p, JW.lowerLegR, 0, 0.1 * k);
      wAdd(p, JW.upperLegL, 2, 0.06 * k);
      wAdd(p, JW.upperLegR, 2, -0.06 * k);
      wAdd(p, JW.spine, 0, 0.06 * k);
      p[POSE_HIPS_OFFSET + 1] = p[POSE_HIPS_OFFSET + 1]! - 0.02 * height * k;
    }
  }
  // Drawing from inside the coat / the hip: the hand goes in, then comes out to the carry or aim.
  const draw = ctx?.draw ?? 1;
  if (draw < 1 && hold !== 'fist') {
    const k = bandW(0, 0.25, draw) * (1 - bandW(0.55, 1, draw));
    const coat = hold === 'pistol' || hold === 'blade';
    wToward(p, JW.upperArmR, 0, coat ? -0.55 : 0.2, k);
    wToward(p, JW.upperArmR, 2, coat ? 0.35 : -0.15, k);
    wToward(p, JW.lowerArmR, 0, coat ? -2.0 : -0.9, k);
    // The wrist keeps the muzzle forward and down as the hand comes up out of the coat.
    if (coat) wToward(p, JW.handR, 0, 1.3, k);
    wAdd(p, JW.chest, 1, 0.2 * k);
    wAdd(p, JW.head, 0, 0.15 * k);
  }
  switch (w.move) {
    case 'swing':
      swing(p, hold, t, height, ctx?.combo ?? 0, still);
      break;
    case 'fire': {
      // Sharp kick, the wrist flips, then a damped settle back onto the target. A small muzzle flip
      // (about 20 degrees, the sawn-off about 30): a bigger one held the barrel at the sky through
      // most of a string of shots.
      const kick = t < 0.12 ? t / 0.12 : (1 - (t - 0.12) / 0.88) ** 2;
      const settle = t > 0.12 ? Math.sin((t - 0.12) * 18) * Math.exp(-(t - 0.12) * 9) * 0.25 : 0;
      const big = hold === 'shotgun' ? 1.45 : 1;
      wAdd(p, JW.upperArmR, 0, (-0.12 * kick + 0.04 * settle) * big);
      wAdd(p, JW.lowerArmR, 0, -0.08 * kick * big);
      wAdd(p, JW.handR, 0, -0.18 * kick * big);
      wAdd(p, JW.upperArmL, 0, -0.1 * kick * big);
      wAdd(p, JW.handL, 0, -0.12 * kick * big);
      wAdd(p, JW.chest, 0, -0.07 * kick * big);
      wAdd(p, JW.chest, 1, 0.05 * kick * big);
      wAdd(p, JW.head, 0, -0.05 * kick * big);
      p[POSE_HIPS_OFFSET + 2] = p[POSE_HIPS_OFFSET + 2]! - 0.025 * height * kick * big;
      break;
    }
    case 'reload': {
      if (hold !== 'pistol' && hold !== 'shotgun') break;
      const k = bandW(0, 0.14, t) * (1 - bandW(0.86, 1, t));
      pull(p, RELOAD[hold], k);
      if (hold === 'pistol') {
        // Swing the cylinder out (wrist roll), tip the muzzle up to dump the cases, thumb rounds in,
        // flick it shut.
        const open = bandW(0.12, 0.22, t) * (1 - bandW(0.78, 0.84, t));
        const tip = bandW(0.22, 0.3, t) * (1 - bandW(0.34, 0.42, t));
        const flick = bandW(0.78, 0.82, t) * (1 - bandW(0.84, 0.92, t));
        wAdd(p, JW.handR, 2, 0.7 * open - 0.3 * flick);
        wAdd(p, JW.lowerArmR, 0, -0.45 * tip);
        wAdd(p, JW.handR, 0, -0.6 * tip);
        const work = bandW(0.42, 0.48, t) * (1 - bandW(0.72, 0.78, t));
        wAdd(p, JW.lowerArmL, 0, 0.22 * Math.sin(t * 46) * work);
        wAdd(p, JW.handL, 2, 0.25 * Math.sin(t * 46 + 1) * work);
      } else {
        // Break the sawn-off (barrels drop), two shells in, snap it shut with a jerk of the wrist.
        const broken = bandW(0.1, 0.2, t) * (1 - bandW(0.74, 0.8, t));
        const snap = bandW(0.76, 0.8, t) * (1 - bandW(0.8, 0.9, t));
        wAdd(p, JW.handR, 0, 0.75 * broken - 0.35 * snap);
        const shell = Math.max(bandW(0.3, 0.38, t) * (1 - bandW(0.42, 0.48, t)), bandW(0.5, 0.58, t) * (1 - bandW(0.62, 0.68, t)));
        wAdd(p, JW.upperArmL, 0, 0.35 * shell);
        wAdd(p, JW.lowerArmL, 0, 0.45 * shell);
        wAdd(p, JW.head, 0, 0.12 * snap);
      }
      wAdd(p, JW.head, 0, 0.4 * k);
      wAdd(p, JW.neck, 0, 0.12 * k);
      break;
    }
    default:
      break;
  }
}

/** Lunge onto the front (left) foot for a strike when standing; `k` 0..1. */
function lunge(p: Pose, k: number, height: number): void {
  if (k <= 0) return;
  wAdd(p, JW.upperLegL, 0, -0.42 * k);
  wAdd(p, JW.lowerLegL, 0, 0.42 * k);
  wAdd(p, JW.footL, 0, -0.02 * k);
  wAdd(p, JW.upperLegR, 0, 0.3 * k);
  wAdd(p, JW.lowerLegR, 0, 0.12 * k);
  wAdd(p, JW.footR, 0, 0.25 * k);
  wAdd(p, JW.spine, 0, 0.1 * k);
  p[POSE_HIPS_OFFSET + 1] = p[POSE_HIPS_OFFSET + 1]! - 0.045 * height * k;
  p[POSE_HIPS_OFFSET + 2] = p[POSE_HIPS_OFFSET + 2]! + 0.06 * height * k;
}

function swing(p: Pose, hold: WeaponHold, t: number, height: number, combo: number, still: number): void {
  const env = bandW(0, 0.12, t) * (1 - bandW(0.82, 1, t));
  if (env <= 0) return;
  const c = ((combo % 3) + 3) % 3;
  switch (hold) {
    case 'fist': {
      // Anticipation (a small pull back), the strike, a hold, recovery. The other hand guards the chin.
      const cock = bandW(0.0, 0.2, t) * (1 - bandW(0.2, 0.3, t));
      const strike = bandW(0.22, 0.36, t) * (1 - bandW(0.55, 0.8, t));
      if (c === 1) {
        // Right cross: the big one, hips and shoulders turn through.
        wToward(p, JW.upperArmR, 0, -0.7 - 0.85 * strike + 0.2 * cock, env);
        wToward(p, JW.upperArmR, 2, 0.25 - 0.15 * strike, env);
        wToward(p, JW.lowerArmR, 0, -1.7 + 1.6 * strike, env);
        wToward(p, JW.upperArmL, 0, -0.75, env * 0.8);
        wToward(p, JW.lowerArmL, 0, -1.85, env * 0.8);
        wToward(p, JW.upperArmL, 2, -0.3, env * 0.8);
        wAdd(p, JW.chest, 1, (0.3 * cock - 0.65 * strike) * env);
        wAdd(p, JW.hips, 1, -0.2 * strike * env);
        wAdd(p, JW.spine, 0, 0.1 * strike * env);
      } else if (c === 2) {
        // Left hook: elbow up, the fist sweeps flat round to the centre.
        const sweep = bandW(0.2, 0.42, t) * (1 - bandW(0.6, 0.85, t));
        wToward(p, JW.upperArmL, 0, -1.25, env);
        wToward(p, JW.upperArmL, 2, 0.9 - 0.75 * sweep, env);
        wToward(p, JW.lowerArmL, 0, -1.55, env);
        wToward(p, JW.lowerArmL, 1, 0.3 - 0.9 * sweep, env);
        wToward(p, JW.upperArmR, 0, -0.75, env * 0.8);
        wToward(p, JW.lowerArmR, 0, -1.9, env * 0.8);
        wToward(p, JW.upperArmR, 2, 0.3, env * 0.8);
        wAdd(p, JW.chest, 1, (-0.3 * cock + 0.6 * sweep) * env);
        wAdd(p, JW.hips, 1, 0.15 * sweep * env);
      } else {
        // Left jab: quick, straight from the guard.
        const jab = bandW(0.18, 0.3, t) * (1 - bandW(0.45, 0.7, t));
        wToward(p, JW.upperArmL, 0, -0.75 - 0.8 * jab + 0.15 * cock, env);
        wToward(p, JW.upperArmL, 2, -0.3 + 0.18 * jab, env);
        wToward(p, JW.lowerArmL, 0, -1.8 + 1.65 * jab, env);
        wToward(p, JW.upperArmR, 0, -0.7, env * 0.8);
        wToward(p, JW.lowerArmR, 0, -1.9, env * 0.8);
        wToward(p, JW.upperArmR, 2, 0.3, env * 0.8);
        wAdd(p, JW.chest, 1, (-0.12 * cock + 0.25 * jab) * env);
      }
      lunge(p, strike * still * (c === 1 ? 1 : 0.6), height);
      break;
    }
    case 'club': {
      if (c === 2) {
        // Overhead chop: raised high over the right shoulder, brought straight down, follow through low.
        const wind = bandW(0.0, 0.38, t) * (1 - bandW(0.38, 0.5, t));
        const strike = bandW(0.38, 0.56, t);
        const follow = 1 - bandW(0.75, 1, t);
        wToward(p, JW.upperArmR, 0, -2.6 * wind - 0.6 * strike * follow, env);
        wToward(p, JW.upperArmR, 2, -0.3 * wind, env);
        wToward(p, JW.lowerArmR, 0, -1.4 * wind - 0.15 * strike * follow, env);
        wToward(p, JW.upperArmL, 0, -2.3 * wind - 0.7 * strike * follow, env * 0.8);
        wToward(p, JW.lowerArmL, 0, -1.3 * wind, env * 0.8);
        wAdd(p, JW.spine, 0, (-0.12 * wind + 0.35 * strike * follow) * env);
        wAdd(p, JW.head, 0, 0.15 * strike * follow * env);
        lunge(p, strike * follow * still, height);
        break;
      }
      // Forehand (right to left) or backhand (left to right): a flat swing that reads from the top camera.
      const dir = c === 0 ? 1 : -1;
      const wind = bandW(0.0, 0.4, t) * (1 - bandW(0.4, 0.52, t));
      const strike = bandW(0.4, 0.58, t);
      const follow = 1 - bandW(0.75, 1, t);
      if (dir > 0) {
        wToward(p, JW.upperArmR, 0, -0.75 * wind + -1.35 * strike * follow, env);
        wToward(p, JW.upperArmR, 2, -1.05 * wind + 0.25 * strike * follow, env);
        wToward(p, JW.lowerArmR, 0, -1.5 * wind + -0.2 * strike * follow, env);
        wToward(p, JW.upperArmL, 0, -0.6 * wind - 1.0 * strike * follow, env * 0.7);
        wToward(p, JW.upperArmL, 2, -0.5 * strike * follow, env * 0.7);
      } else {
        // Backhand: wound across the body to the left, swept out to the right.
        wToward(p, JW.upperArmR, 0, -1.2 * wind - 1.3 * strike * follow, env);
        wToward(p, JW.upperArmR, 2, 0.75 * wind - 0.9 * strike * follow, env);
        wToward(p, JW.lowerArmR, 0, -1.2 * wind - 0.1 * strike * follow, env);
        wToward(p, JW.upperArmL, 0, -0.9 * wind - 0.4 * strike * follow, env * 0.7);
        wToward(p, JW.upperArmL, 2, -0.2 * wind, env * 0.7);
      }
      wAdd(p, JW.chest, 1, dir * (-0.75 * wind + 0.85 * strike * follow) * env);
      wAdd(p, JW.spine, 1, dir * (-0.25 * wind + 0.3 * strike * follow) * env);
      wAdd(p, JW.spine, 0, 0.15 * strike * follow * env);
      p[POSE_HIPS_OFFSET + 1] = p[POSE_HIPS_OFFSET + 1]! - 0.03 * height * strike * follow * env;
      lunge(p, strike * follow * still * 0.8, height);
      break;
    }
    case 'blade': {
      if (c === 1) {
        // Slash: back-hand cut across at chest height.
        const draw = bandW(0.0, 0.3, t) * (1 - bandW(0.3, 0.4, t));
        const cut = bandW(0.3, 0.46, t) * (1 - bandW(0.62, 0.86, t));
        wToward(p, JW.upperArmR, 0, -1.0 * draw - 1.25 * cut, env);
        wToward(p, JW.upperArmR, 2, 0.6 * draw - 0.7 * cut, env);
        wToward(p, JW.lowerArmR, 0, -1.4 * draw - 0.2 * cut, env);
        wAdd(p, JW.chest, 1, (0.35 * draw - 0.45 * cut) * env);
        lunge(p, cut * still * 0.6, height);
        break;
      }
      // Thrust: short draw back, a low fast stab, recover.
      const draw = bandW(0.0, 0.3, t) * (1 - bandW(0.3, 0.42, t));
      const thrust = bandW(0.3, 0.44, t) * (1 - bandW(0.6, 0.85, t));
      wToward(p, JW.upperArmR, 0, 0.35 * draw - 1.3 * thrust, env);
      wToward(p, JW.upperArmR, 2, -0.2 * draw + 0.15 * thrust, env);
      wToward(p, JW.lowerArmR, 0, -1.6 * draw - 0.1 * thrust, env);
      wAdd(p, JW.chest, 1, (0.3 * draw - 0.35 * thrust) * env);
      wAdd(p, JW.spine, 0, 0.12 * thrust * env);
      lunge(p, thrust * still, height);
      break;
    }
    default: {
      // Pistol-whip / butt stroke with a gun in hand: a short chop.
      const chop = bandW(0.2, 0.45, t) * (1 - bandW(0.6, 0.9, t));
      wToward(p, JW.upperArmR, 0, -1.9 + 0.9 * chop, env);
      wToward(p, JW.lowerArmR, 0, -0.9 + 0.6 * chop, env);
      wAdd(p, JW.chest, 1, -0.3 * chop * env);
      lunge(p, chop * still * 0.5, height);
      break;
    }
  }
}


export function createCharacterAnimator(opts: AnimatorOptions = {}): CharacterAnimator {
  const skeleton = createSkeleton(opts.build ?? 'average', opts.height ?? 1, opts.sculpt);
  const dims = skeleton.dims;
  const h = dims.height;
  const visualScale = opts.scale ?? 1;
  const legLen = dims.upperLeg + dims.lowerLeg;
  // The authored player's narrower shoulders and slimmer coat let the arms hang closer in.
  const armOut = 0.08 + 0.2 * (dims.width - 1) - (opts.sculpt === 'villain' ? 0.025 : 0);

  const out = createPose();
  const base = createPose();
  const toPose = createPose();
  const fromPose = createPose();
  const scratch = createPose();

  // --- authored key poses (built once) ---------------------------------------------------------
  function rest(p: Pose): void {
    p.fill(0);
    p[Joint.upperArmL * 3 + 2] = armOut;
    p[Joint.upperArmR * 3 + 2] = -armOut;
    p[Joint.lowerArmL * 3] = -0.12;
    p[Joint.lowerArmR * 3] = -0.12;
    p[Joint.handL * 3] = -0.05;
    p[Joint.handR * 3] = -0.05;
  }
  function key(entries: readonly (readonly [number, number, number, number])[], ox: number, oy: number, oz: number): Pose {
    const p = createPose();
    rest(p);
    for (const [j, x, y, z] of entries) {
      p[j * 3] = x;
      p[j * 3 + 1] = y;
      p[j * 3 + 2] = z;
    }
    p[POSE_HIPS_OFFSET] = ox * h;
    p[POSE_HIPS_OFFSET + 1] = oy * h;
    p[POSE_HIPS_OFFSET + 2] = oz * h;
    return p;
  }
  const J = Joint;
  const STAND = key([], 0, 0, 0);
  const AIRBORNE = key(
    [
      [J.hips, -0.75, 0, 0],
      [J.spine, -0.15, 0, 0],
      [J.head, 0.35, 0, 0],
      [J.upperArmL, -1.3, 0, 0.9],
      [J.upperArmR, -1.3, 0, -0.9],
      [J.lowerArmL, -0.5, 0, 0],
      [J.lowerArmR, -0.5, 0, 0],
      [J.upperLegL, -0.6, 0, 0.1],
      [J.upperLegR, -0.4, 0, -0.1],
      [J.lowerLegL, 0.5, 0, 0],
      [J.lowerLegR, 0.7, 0, 0],
    ],
    0,
    -0.28,
    -0.1,
  );
  const LYING = key(
    [
      [J.hips, -1.48, 0, 0],
      [J.spine, 0.05, 0, 0],
      [J.chest, 0.05, 0, 0],
      [J.neck, 0.12, 0, 0],
      [J.head, 0.12, 0.35, 0],
      [J.upperArmL, -0.4, 0, 1.1],
      [J.upperArmR, -0.3, 0, -1.0],
      [J.lowerArmL, -0.45, 0, 0],
      [J.lowerArmR, -0.3, 0, 0],
      [J.upperLegL, -0.22, 0, 0.14],
      [J.upperLegR, -0.05, 0, -0.1],
      [J.lowerLegL, 0.45, 0, 0],
      [J.lowerLegR, 0.1, 0, 0],
      [J.footL, 0.35, 0, 0],
      [J.footR, 0.5, 0, 0],
    ],
    0,
    -0.78,
    -0.12,
  );
  const SIT = key(
    [
      [J.spine, 0.35, 0, 0],
      [J.chest, 0.15, 0, 0],
      [J.head, -0.2, 0, 0],
      [J.upperArmL, 0.55, 0, 0.25],
      [J.upperArmR, 0.55, 0, -0.25],
      [J.lowerArmL, -0.1, 0, 0],
      [J.lowerArmR, -0.1, 0, 0],
      [J.upperLegL, -1.45, 0, 0.1],
      [J.upperLegR, -1.45, 0, -0.1],
      [J.lowerLegL, 0.6, 0, 0],
      [J.lowerLegR, 0.5, 0, 0],
      [J.footL, 0.5, 0, 0],
      [J.footR, 0.5, 0, 0],
    ],
    0,
    -0.72,
    -0.15,
  );
  const KNEEL_UP = key(
    [
      [J.spine, 0.45, 0, 0],
      [J.chest, 0.1, 0, 0],
      [J.head, -0.35, 0, 0],
      [J.upperArmL, -0.6, 0, 0.15],
      [J.lowerArmL, -0.6, 0, 0],
      [J.upperArmR, 0.1, 0, -0.15],
      [J.upperLegL, -1.45, 0, 0],
      [J.lowerLegL, 1.5, 0, 0],
      [J.footL, -0.05, 0, 0],
      [J.upperLegR, 0.05, 0, 0],
      [J.lowerLegR, 1.55, 0, 0],
      [J.footR, 0.6, 0, 0],
    ],
    0,
    -0.42,
    0,
  );
  const handsUp: (readonly [number, number, number, number])[] = [
    [J.chest, -0.08, 0, 0],
    [J.head, 0.08, 0, 0],
    [J.shoulderL, 0, 0, 0.12],
    [J.shoulderR, 0, 0, -0.12],
    [J.upperArmL, -0.25, 0, 1.45],
    [J.upperArmR, -0.25, 0, -1.45],
    [J.lowerArmL, 0, 0, 1.5],
    [J.lowerArmR, 0, 0, -1.5],
    [J.handL, 0, 0, 0.1],
    [J.handR, 0, 0, -0.1],
  ];
  const HANDS_UP = key(handsUp, 0, 0, 0);
  const KNEEL_SURRENDER = key(
    [
      ...handsUp,
      [J.head, 0.3, 0, 0],
      [J.spine, 0.05, 0, 0],
      [J.upperLegL, 0.05, 0, 0.06],
      [J.upperLegR, 0.05, 0, -0.06],
      [J.lowerLegL, 1.55, 0, 0],
      [J.lowerLegR, 1.55, 0, 0],
      [J.footL, 0.7, 0, 0],
      [J.footR, 0.7, 0, 0],
    ],
    0,
    -0.43,
    0,
  );
  // Hit: doubled over, both hands clutching the stomach.
  const BUCKLE = key(
    [
      [J.hips, 0.2, 0, 0],
      [J.spine, 0.3, 0, 0],
      [J.chest, 0.2, 0, 0],
      [J.head, 0.4, 0, 0.1],
      [J.upperArmL, -0.35, -0.6, 0.12],
      [J.upperArmR, -0.35, 0.6, -0.12],
      [J.lowerArmL, -1.45, 0, 0],
      [J.lowerArmR, -1.35, 0, 0],
      [J.upperLegL, -0.7, 0, 0.05],
      [J.upperLegR, -0.75, 0, -0.05],
      [J.lowerLegL, 1.2, 0, 0],
      [J.lowerLegR, 1.25, 0, 0],
      [J.footL, -0.4, 0, 0],
      [J.footR, -0.4, 0, 0],
    ],
    0,
    -0.28,
    0,
  );
  // Down on the knees, slumped forward, before going over.
  const KNEEL_SLUMP = key(
    [
      [J.spine, 0.55, 0, 0],
      [J.chest, 0.25, 0, 0],
      [J.neck, 0.2, 0, 0],
      [J.head, 0.45, 0.2, 0.15],
      [J.upperArmL, -0.2, -0.3, 0.2],
      [J.upperArmR, -0.1, 0.3, -0.15],
      [J.lowerArmL, -0.9, 0, 0],
      [J.lowerArmR, -0.6, 0, 0],
      [J.upperLegL, -0.15, 0, 0.08],
      [J.upperLegR, -0.05, 0, -0.08],
      [J.lowerLegL, 1.6, 0, 0],
      [J.lowerLegR, 1.5, 0, 0],
      [J.footL, 0.7, 0, 0],
      [J.footR, 0.7, 0, 0],
    ],
    0,
    -0.42,
    0.02,
  );
  const COWER = key(
    [
      [J.spine, 0.62, 0, 0],
      [J.chest, 0.25, 0, 0],
      [J.neck, 0.2, 0, 0],
      [J.head, 0.35, 0, 0],
      [J.upperArmL, -2.35, 0, -0.25],
      [J.upperArmR, -2.35, 0, 0.25],
      [J.lowerArmL, -1.75, 0, 0],
      [J.lowerArmR, -1.75, 0, 0],
      [J.upperLegL, -1.25, 0, 0.18],
      [J.upperLegR, -1.1, 0, -0.18],
      [J.lowerLegL, 1.75, 0, 0],
      [J.lowerLegR, 1.6, 0, 0],
      [J.footL, -0.45, 0, 0],
      [J.footR, -0.4, 0, 0],
    ],
    0,
    -0.4,
    -0.06,
  );
  const FACE_DOWN = key(
    [
      [J.hips, 1.5, 0, 0.04],
      [J.spine, 0.02, 0, 0],
      [J.head, -0.25, 1.15, 0],
      [J.upperArmL, -0.6, 0, 0.9],
      [J.upperArmR, -2.4, 0, -0.4],
      [J.lowerArmL, -0.3, 0, 0],
      [J.lowerArmR, -0.25, 0, 0],
      [J.upperLegL, 0, 0, 0.15],
      [J.upperLegR, -0.2, 0, -0.1],
      [J.lowerLegR, 0.6, 0, 0],
      [J.footL, 0.9, 0, 0],
      [J.footR, 0.7, 0, 0],
    ],
    0,
    -0.8,
    0.2,
  );

  // --- machine state ------------------------------------------------------------------------------
  let state: AnimState = 'idle';
  let previousState: AnimState = 'idle';
  let stateTime = 0;
  let fromState: AnimState = 'idle';
  let fromTime = 0;
  let fromSnapshot = false;
  let blendT = 0;
  let blendDur = 0;
  const weights = { from: 0, to: 1 };
  let phase = 0;
  let clock = 0;
  let turnPhase = 0;
  let stepsThisFrame = 0;
  // Smoothed signals.
  let lastSpeed = 0;
  let speedS = 0;
  let accelS = 0;
  let turnS = 0;
  let sprintW = 0;
  let locoW = 0;
  let stopIntensity = 0.5;
  // Idle variation.
  let seed = (opts.seed ?? 0x2f6b3d1) >>> 0;
  let shift = 0;
  let shiftTarget = 0;
  let shiftTimer = 2;
  let look = 0;
  let lookTarget = 0;
  let lookTimer = 3;
  // Coat-tail spring (x = trail, z = sway).
  let coatX = 0;
  let coatVX = 0;
  let coatZ = 0;
  let coatVZ = 0;
  // Current input, readable by the evaluators.
  let inSpeed = 0;
  let inRunning = false;
  let inTurn = 0;
  let inProgress = 0;
  /** Speed the evaluators use: the live speed for the incoming state, frozen for the outgoing one. */
  let evalSpeed = 0;
  let fromSpeed = 0;
  let lastProgress = 0;
  let evalProgress = 0;
  let fromProgress = 0;
  const step: FootstepEvent = { foot: 'left', dx: 0, dz: 0, speed: 0, intensity: 0 };
  // Character habits.
  const traits: AnimTraits = { age: 0.3, female: false, smoker: false, swagger: 0, bulk: 0 };
  /** Stride length factor from the traits (1 for the default character, so the stride tests hold). */
  const strideK = (): number => 1 + 0.1 * traits.swagger + 0.06 * traits.bulk - 0.12 * (traits.age > 0.6 ? (traits.age - 0.6) / 0.4 : 0);
  // Exertion: builds while running, decays standing; drives breathing (and hands on knees).
  let exert = 0;
  // Idle habits: the current act, its clock and length, and its eased weight.
  let act: IdleAct = 'none';
  let actT = 0;
  let actDur = 0;
  let actW = 0;
  let shownAct: IdleAct = 'none';
  let actWait = 2 + ((opts.seed ?? 0) % 7) * 0.6;
  let social = 0;
  let talking = false;
  // Head look-at (smoothed yaw).
  let lookS = 0;
  // Weapon layer bookkeeping: combos, drawing.
  let combo = 0;
  let prevMove: WeaponAnim['move'] = 'none';
  let prevT = 0;
  let lastSwingEnd = -9;
  let prevHold: WeaponHold = 'none';
  let drawT = 1;
  const wctx: WeaponLayerContext = { combo: 0, still: 0, draw: 1 };

  function rand(): number {
    // mulberry32: deterministic per animator.
    seed = (seed + 0x6d2b79f5) | 0;
    let t = seed;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  }

  // --- pose helpers -------------------------------------------------------------------------------
  const add = (p: Pose, j: number, axis: number, v: number): void => {
    p[j * 3 + axis] = p[j * 3 + axis]! + v;
  };
  const toward = (p: Pose, j: number, axis: number, v: number, w: number): void => {
    const i = j * 3 + axis;
    p[i] = p[i]! + (v - p[i]!) * w;
  };
  function lerpPose(dst: Pose, a: Pose, b: Pose, t: number): void {
    for (let i = 0; i < POSE_SIZE; i++) dst[i] = a[i]! + (b[i]! - a[i]!) * t;
  }
  function gaitAmount(speed: number): number {
    return clamp01((speed - 1.2) / 5.4);
  }
  function stanceFraction(g: number): number {
    return 0.62 + (0.4 - 0.62) * g;
  }
  /** Stance half-travel of the foot (character units) that matches ground speed. */
  function stanceAmplitude(speed: number): number {
    const g = gaitAmount(speed);
    const a = (stanceFraction(g) * cycleLength(speed, h) * strideK() * 0.5) / visualScale;
    return Math.min(a, legLen * 0.72) * clamp01(speed / 0.8);
  }

  /** Writes one leg; returns the foot's normalised fore-aft position (-1 back … +1 front). */
  function leg(p: Pose, ju: number, jl: number, jf: number, u: number, a: number, f: number, g: number, move: number): number {
    let x: number;
    let knee: number;
    let toe = 0;
    let flat = 1;
    if (u < f) {
      const s = u / f;
      x = a * (1 - 2 * s);
      knee = 0.04 + (0.06 + 0.3 * g) * Math.sin(Math.PI * s) * move;
      // Foot roll: toes up at heel strike, heel peeling off before the push.
      toe = (-0.24 * (1 - smooth(s / 0.16)) * (1 - 0.6 * g) + 0.28 * smooth((s - 0.76) / 0.24)) * move;
    } else {
      const s = (u - f) / (1 - f);
      x = a * (-1 + 2 * smooth(s));
      const lift = Math.sin(Math.PI * s);
      knee = 0.04 + (0.75 + 0.8 * g) * lift * move;
      // Toe-off carries into the swing; the toes come up again ready for the heel strike.
      toe = (0.3 * lift + 0.28 * (1 - smooth(s / 0.25)) - 0.24 * smooth((s - 0.7) / 0.3) * (1 - 0.6 * g)) * move;
      flat = 1 - 0.5 * lift;
    }
    const thigh = -Math.asin(clamp(x / legLen, -0.85, 0.85)) - knee * 0.45;
    p[ju * 3] = thigh;
    p[jl * 3] = knee;
    p[jf * 3] = -(thigh + knee) * flat + toe;
    return a > 1e-4 ? x / a : 0;
  }

  /** Arm counter-swing: `n` is the opposite leg's fore-aft position. Elbows bend more with pace. */
  function armSwing(p: Pose, ua: number, la: number, side: number, n: number, amp: number, g: number, move: number): void {
    let swing = -amp * n;
    if (swing > 0) swing *= 0.7;
    p[ua * 3] = swing;
    p[ua * 3 + 2] = side * (armOut + 0.06 * g);
    p[la * 3] = -(0.15 + 1.15 * g) - 0.25 * Math.max(0, -swing) * move;
  }

  function evalLoco(p: Pose, speed: number): void {
    rest(p);
    const g = gaitAmount(speed);
    const move = clamp01(speed / 0.8);
    const f = stanceFraction(g);
    const a = stanceAmplitude(speed);
    const nL = leg(p, J.upperLegL, J.lowerLegL, J.footL, phase, a, f, g, move);
    const nR = leg(p, J.upperLegR, J.lowerLegR, J.footR, (phase + 0.5) % 1, a, f, g, move);
    const diff = (nL - nR) * 0.5;
    const old = traits.age > 0.6 ? (traits.age - 0.6) / 0.4 : 0;
    const fem = traits.female ? 1 : 0;
    const sw = traits.swagger * (1 - 0.5 * g);
    const bulk = traits.bulk;
    // Pelvis twists with the stride (more in a woman's walk), shoulders counter-twist (more with a
    // swagger); arms counter-swing the legs.
    const tw = (0.1 + 0.08 * g + 0.06 * fem * (1 - g)) * move;
    p[J.hips * 3 + 1] = -tw * diff;
    p[J.chest * 3 + 1] = tw * (1.7 - 0.6 * fem + 0.9 * sw) * diff;
    p[J.hips * 3 + 2] = (0.035 + 0.035 * fem + 0.02 * bulk) * (1 - g) * Math.sin(TAU * phase) * move;
    const amp = (0.32 + 0.55 * g) * move * (1 - 0.45 * old) * (1 - 0.25 * fem * (1 - g)) * (1 + 0.3 * sw);
    armSwing(p, J.upperArmL, J.lowerArmL, 1, -diff, amp, g, move);
    armSwing(p, J.upperArmR, J.lowerArmR, -1, diff, amp, g, move);
    // Follow-through: forearms trail the upper arm's swing a beat behind.
    const lag = (0.12 + 0.18 * g) * move * Math.sin(TAU * (phase - 0.12));
    p[J.lowerArmL * 3] = p[J.lowerArmL * 3]! - Math.max(0, lag);
    p[J.lowerArmR * 3] = p[J.lowerArmR * 3]! - Math.max(0, -lag);
    p[J.handL * 3] = -0.05 + 0.12 * lag * move;
    p[J.handR * 3] = -0.05 - 0.12 * lag * move;
    if (fem) {
      p[J.upperArmL * 3 + 2] = p[J.upperArmL * 3 + 2]! - 0.03;
      p[J.upperArmR * 3 + 2] = p[J.upperArmR * 3 + 2]! + 0.03;
    }
    // Swagger: elbows out and bent, the shoulders roll with the stride, the head rolls a touch the
    // other way. Bulk: arms carried away from the body, the feet set wider.
    const out = 0.09 * sw + 0.06 * bulk;
    if (out > 0) {
      p[J.upperArmL * 3 + 2] = p[J.upperArmL * 3 + 2]! + out;
      p[J.upperArmR * 3 + 2] = p[J.upperArmR * 3 + 2]! - out;
      p[J.lowerArmL * 3] = p[J.lowerArmL * 3]! - 0.3 * sw * move;
      p[J.lowerArmR * 3] = p[J.lowerArmR * 3]! - 0.3 * sw * move;
      const roll = 0.07 * sw * Math.sin(TAU * phase) * move;
      p[J.chest * 3 + 2] = roll;
      p[J.head * 3 + 2] = -roll * 0.5;
      p[J.upperLegL * 3 + 2] = p[J.upperLegL * 3 + 2]! + 0.05 * bulk;
      p[J.upperLegR * 3 + 2] = p[J.upperLegR * 3 + 2]! - 0.05 * bulk;
    }
    // Lean: speed, sprint, acceleration; an old man's stoop; a swaggerer walks chest-first.
    const lean = (0.03 + 0.1 * g) * move + 0.17 * sprintW + clamp(accelS * 0.025, -0.15, 0.2) + 0.18 * old - 0.05 * sw * move;
    p[J.spine * 3] = lean;
    p[J.chest * 3] = 0.02 + 0.04 * g + 0.1 * old - 0.03 * sw;
    // Heaving chest when blown.
    p[J.chest * 3] = p[J.chest * 3]! - 0.04 * exert * Math.sin(TAU * clock * 1.6);
    // Vertical bob: high at mid-stance, lower overall when running; a heavy man bobs more.
    const bobAmp = (0.016 + 0.024 * g) * move * h * (1 + 0.35 * bulk + 0.2 * sw);
    p[POSE_HIPS_OFFSET + 1] = -(0.012 + 0.05 * g) * move * h + bobAmp * Math.cos(2 * TAU * (phase - f * 0.5));
    // Head stabilisation: cancel torso pitch and yaw so the gaze stays level and forward.
    const pitch = p[J.spine * 3]! + p[J.chest * 3]!;
    p[J.neck * 3] = -pitch * 0.45;
    p[J.head * 3] = -pitch * 0.4;
    p[J.head * 3 + 1] = -(p[J.hips * 3 + 1]! + p[J.chest * 3 + 1]!) * 0.85;
  }

  function evalIdle(p: Pose): void {
    rest(p);
    // Breathing: slow and shallow at rest, quick and deep when blown.
    const breathRate = 3.8 - 2.4 * exert;
    breathPhase = breathPhaseAt(clock, breathRate);
    const breath = Math.sin(breathPhase);
    const deep = 1 + 2.5 * exert;
    p[J.chest * 3] = -0.015 * breath * deep;
    p[J.shoulderL * 3 + 2] = 0.02 * breath * deep;
    p[J.shoulderR * 3 + 2] = -0.02 * breath * deep;
    p[J.upperArmL * 3] = 0.03 * breath;
    p[J.upperArmR * 3] = 0.03 * breath;
    const old = traits.age > 0.6 ? (traits.age - 0.6) / 0.4 : 0;
    if (old > 0) {
      // Stooped, head pushed forward, knees soft.
      p[J.spine * 3] = 0.16 * old;
      p[J.chest * 3] = p[J.chest * 3]! + 0.1 * old;
      p[J.neck * 3] = -0.12 * old;
      p[J.head * 3] = -0.1 * old;
    }
    // Weight shift: pelvis slides over the standing leg, drops on the free side, free knee bends.
    const s = shift;
    const sx = 0.035 * s * dims.width * h;
    const roll = 0.05 * s;
    p[POSE_HIPS_OFFSET] = sx;
    p[POSE_HIPS_OFFSET + 1] = (-0.008 + 0.004 * breath - 0.01 * Math.abs(s)) * h;
    p[J.hips * 3 + 2] = roll;
    p[J.spine * 3 + 2] = -roll * 1.3;
    p[J.head * 3 + 2] = roll * 0.4;
    const legZ = -roll - sx / legLen;
    p[J.upperLegL * 3 + 2] = legZ;
    p[J.upperLegR * 3 + 2] = legZ;
    const freeR = Math.max(0, s);
    const freeL = Math.max(0, -s);
    p[J.upperLegR * 3] = -0.09 * freeR;
    p[J.lowerLegR * 3] = 0.18 * freeR;
    p[J.footR * 3] = -0.09 * freeR;
    p[J.upperLegL * 3] = -0.09 * freeL;
    p[J.lowerLegL * 3] = 0.18 * freeL;
    p[J.footL * 3] = -0.09 * freeL;
    if (traits.female) {
      // Hip out over the standing leg, feet closer, arms carried in.
      p[J.hips * 3 + 2] = p[J.hips * 3 + 2]! * 1.6;
      p[J.upperArmL * 3 + 2] = p[J.upperArmL * 3 + 2]! - 0.03;
      p[J.upperArmR * 3 + 2] = p[J.upperArmR * 3 + 2]! + 0.03;
      p[J.lowerArmL * 3] = -0.3;
      p[J.lowerArmR * 3] = -0.3;
    }
    // Swagger: chest up, weight back on the heels, elbows out. Bulk: feet planted wide.
    const sw = traits.swagger;
    const bulk = traits.bulk;
    if (sw > 0 || bulk > 0) {
      p[J.spine * 3] = p[J.spine * 3]! - 0.05 * sw;
      p[J.chest * 3] = p[J.chest * 3]! - 0.04 * sw;
      p[J.head * 3] = p[J.head * 3]! + 0.06 * sw;
      p[J.upperArmL * 3 + 2] = p[J.upperArmL * 3 + 2]! + 0.1 * sw + 0.07 * bulk;
      p[J.upperArmR * 3 + 2] = p[J.upperArmR * 3 + 2]! - 0.1 * sw - 0.07 * bulk;
      p[J.lowerArmL * 3] = p[J.lowerArmL * 3]! - 0.2 * sw;
      p[J.lowerArmR * 3] = p[J.lowerArmR * 3]! - 0.2 * sw;
      p[J.upperLegL * 3 + 2] = p[J.upperLegL * 3 + 2]! + 0.06 * bulk + 0.04 * sw;
      p[J.upperLegR * 3 + 2] = p[J.upperLegR * 3 + 2]! - 0.06 * bulk - 0.04 * sw;
      p[POSE_HIPS_OFFSET + 2] = p[POSE_HIPS_OFFSET + 2]! - 0.015 * sw * h;
    }
    // Glance around.
    p[J.neck * 3 + 1] = 0.12 * look;
    p[J.head * 3 + 1] = 0.3 * look;
    if (actW > 1e-3) applyAct(p, shownAct, actT, actDur, actW);
  }

  /** Continuous breathing phase (rad) whose rate can change without jumping. */
  let breathPhase = 0;
  let breathClock = 0;
  let breathAcc = 0;
  function breathPhaseAt(now: number, period: number): number {
    if (now !== breathClock) {
      breathAcc += ((now - breathClock) * TAU) / period;
      breathClock = now;
    }
    return breathAcc;
  }

  /** Idle habits, pulled in with weight `w` (eased by the scheduler in update()). */
  function applyAct(p: Pose, a: IdleAct, t: number, dur: number, w: number): void {
    const env = w * band(0, 0.35, t) * (1 - band(dur - 0.35, dur, t));
    if (env <= 0) return;
    switch (a) {
      case 'watch': {
        // Shoot the cuff and look at the wrist watch.
        toward(p, J.upperArmL, 0, -0.55, env);
        toward(p, J.upperArmL, 1, -0.5, env);
        toward(p, J.upperArmL, 2, 0.12, env);
        toward(p, J.lowerArmL, 0, -1.75, env);
        toward(p, J.handL, 2, -0.6, env);
        toward(p, J.head, 0, 0.42, env);
        toward(p, J.head, 1, 0.22, env);
        toward(p, J.neck, 0, 0.1, env);
        break;
      }
      case 'smoke': {
        // A drag every few seconds: hand to the mouth, hold, down, blow out.
        const cyc = (t % 2.6) / 2.6;
        const up = band(0.05, 0.3, cyc) * (1 - band(0.5, 0.75, cyc));
        toward(p, J.upperArmR, 0, -0.35 - 0.35 * up, env);
        toward(p, J.upperArmR, 1, 0.55 * up, env);
        toward(p, J.upperArmR, 2, -0.05 + 0.2 * up, env);
        toward(p, J.lowerArmR, 0, -0.9 - 1.4 * up, env);
        toward(p, J.upperArmL, 0, 0.05, env);
        toward(p, J.lowerArmL, 0, -0.5, env * 0.6);
        add(p, J.head, 0, (-0.12 * band(0.65, 0.8, cyc) * (1 - band(0.85, 1, cyc)) - 0.05 * up) * env);
        break;
      }
      case 'pockets': {
        // Hands in the trouser pockets, elbows out.
        for (const [ua, la, s] of [
          [J.upperArmL, J.lowerArmL, 1],
          [J.upperArmR, J.lowerArmR, -1],
        ] as const) {
          toward(p, ua, 0, 0.12, env);
          toward(p, ua, 2, s * 0.32, env);
          toward(p, la, 0, -0.55, env);
          toward(p, la, 1, s * -0.4, env);
        }
        break;
      }
      case 'fold': {
        // Arms folded across the chest.
        toward(p, J.upperArmL, 0, -0.42, env);
        toward(p, J.upperArmL, 1, -1.05, env);
        toward(p, J.upperArmL, 2, 0.06, env);
        toward(p, J.lowerArmL, 0, -1.8, env);
        toward(p, J.upperArmR, 0, -0.5, env);
        toward(p, J.upperArmR, 1, 1.05, env);
        toward(p, J.upperArmR, 2, -0.06, env);
        toward(p, J.lowerArmR, 0, -1.7, env);
        add(p, J.head, 0, -0.04 * env);
        break;
      }
      case 'behind': {
        // Hands clasped behind the back (the old boys' stroll pose).
        toward(p, J.upperArmL, 0, 0.42, env);
        toward(p, J.upperArmL, 1, 0.9, env);
        toward(p, J.lowerArmL, 0, -1.3, env);
        toward(p, J.upperArmR, 0, 0.42, env);
        toward(p, J.upperArmR, 1, -0.9, env);
        toward(p, J.lowerArmR, 0, -1.3, env);
        break;
      }
      case 'scratch': {
        // Scratch the back of the head.
        toward(p, J.upperArmR, 0, -2.2, env);
        toward(p, J.upperArmR, 2, -0.55, env);
        toward(p, J.lowerArmR, 0, -1.9 + 0.2 * Math.sin(t * 22), env);
        add(p, J.head, 0, 0.12 * env);
        add(p, J.head, 2, 0.1 * env);
        break;
      }
      case 'talk': {
        // Talking with the hands: the forearms come up and work, the head bobs with the words.
        const a1 = Math.sin(t * 4.1);
        const a2 = Math.sin(t * 2.7 + 1.3);
        toward(p, J.upperArmR, 0, -0.45 - 0.15 * a2, env);
        toward(p, J.upperArmR, 1, 0.5, env);
        toward(p, J.lowerArmR, 0, -1.25 - 0.3 * a1, env);
        toward(p, J.handR, 2, 0.4 + 0.25 * a1, env);
        toward(p, J.upperArmL, 0, -0.25 - 0.2 * Math.max(0, a2), env * 0.8);
        toward(p, J.lowerArmL, 0, -0.8 - 0.4 * Math.max(0, a2), env * 0.8);
        add(p, J.head, 0, 0.05 * Math.sin(t * 6.3) * env);
        add(p, J.head, 1, 0.12 * Math.sin(t * 1.7) * env);
        add(p, J.chest, 1, 0.06 * a2 * env);
        break;
      }
      case 'listen': {
        // Listening: weight back, arms folded or in pockets, a nod now and then, a laugh.
        applyAct(p, (Math.floor(actDur * 10) % 2 ? 'fold' : 'pockets') as IdleAct, t, dur, w);
        const nod = band(0.0, 0.2, (t % 2.3) / 2.3) * (1 - band(0.25, 0.45, (t % 2.3) / 2.3));
        add(p, J.head, 0, 0.14 * nod * env);
        const laugh = band(1.2, 1.4, t) * (1 - band(2.0, 2.3, t));
        add(p, J.chest, 0, -0.04 * laugh * Math.abs(Math.sin(t * 14)) * env);
        add(p, J.head, 0, -0.1 * laugh * env);
        break;
      }
      case 'hips': {
        // Hands on the hips, elbows out, weight on one leg; a look about.
        for (const [ua, la, s] of [
          [J.upperArmL, J.lowerArmL, 1],
          [J.upperArmR, J.lowerArmR, -1],
        ] as const) {
          toward(p, ua, 0, 0.2, env);
          toward(p, ua, 1, s * -0.35, env);
          toward(p, ua, 2, s * 0.6, env);
          toward(p, la, 0, -1.35, env);
          toward(p, la, 1, s * -0.5, env);
        }
        add(p, J.chest, 0, -0.04 * env);
        add(p, J.head, 1, 0.2 * Math.sin(t * 0.9) * env);
        break;
      }
      case 'yawn': {
        // A yawn and a stretch: both arms up and back, head back, then a shake of the shoulders.
        const up = band(0.1, 0.6, t) * (1 - band(dur - 0.9, dur - 0.4, t));
        toward(p, J.upperArmL, 0, -2.4 * up, env);
        toward(p, J.upperArmR, 0, -2.4 * up, env);
        toward(p, J.upperArmL, 2, 0.5 * up, env);
        toward(p, J.upperArmR, 2, -0.5 * up, env);
        toward(p, J.lowerArmL, 0, -0.6 + 0.4 * up, env);
        toward(p, J.lowerArmR, 0, -0.6 + 0.4 * up, env);
        add(p, J.head, 0, -0.35 * up * env);
        add(p, J.spine, 0, -0.1 * up * env);
        const shake = band(dur - 0.5, dur - 0.3, t) * (1 - band(dur - 0.15, dur, t));
        add(p, J.chest, 2, 0.06 * Math.sin(t * 28) * shake * env);
        break;
      }
      case 'collar': {
        // A tug at the lapels to set the coat straight, then the collar turned up with both hands.
        const lap = band(0.1, 0.4, t) * (1 - band(1.0, 1.3, t));
        const col = band(1.2, 1.5, t) * (1 - band(dur - 0.5, dur - 0.2, t));
        for (const [ua, la, s] of [
          [J.upperArmL, J.lowerArmL, 1],
          [J.upperArmR, J.lowerArmR, -1],
        ] as const) {
          toward(p, ua, 0, -0.5 * lap - 0.95 * col, env);
          toward(p, ua, 1, s * (-0.4 * lap - 0.9 * col), env);
          toward(p, ua, 2, s * (0.1 * lap + 0.45 * col), env);
          toward(p, la, 0, -2.0 * lap - 2.3 * col, env);
        }
        add(p, J.spine, 0, (0.06 * lap - 0.04 * col) * env);
        add(p, J.chest, 0, -0.08 * lap * env);
        add(p, J.head, 0, (0.15 * lap + 0.05 * col + 0.1 * Math.sin(t * 9) * lap) * env);
        add(p, J.head, 1, 0.25 * col * Math.sin(t * 1.3) * env);
        break;
      }
      case 'winded': {
        // Blown after a run: bent over, hands on the knees, heaving.
        const heave = Math.sin(t * 9);
        toward(p, J.spine, 0, 0.55, env);
        toward(p, J.chest, 0, 0.18 + 0.05 * heave, env);
        toward(p, J.head, 0, -0.45, env);
        toward(p, J.upperArmL, 0, -0.75, env);
        toward(p, J.upperArmR, 0, -0.75, env);
        toward(p, J.lowerArmL, 0, -0.2, env);
        toward(p, J.lowerArmR, 0, -0.2, env);
        toward(p, J.upperLegL, 0, -0.35, env);
        toward(p, J.upperLegR, 0, -0.35, env);
        toward(p, J.lowerLegL, 0, 0.45, env);
        toward(p, J.lowerLegR, 0, 0.45, env);
        toward(p, J.footL, 0, -0.1, env);
        toward(p, J.footR, 0, -0.1, env);
        p[POSE_HIPS_OFFSET + 1] = p[POSE_HIPS_OFFSET + 1]! - 0.07 * h * env;
        p[POSE_HIPS_OFFSET + 2] = p[POSE_HIPS_OFFSET + 2]! - 0.04 * h * env;
        break;
      }
      default:
        break;
    }
  }

  /** Pick the next idle habit (deterministic per seed). */
  function nextAct(): void {
    if (social > 0.5) {
      talking = !talking && rand() < 0.75 ? true : !talking ? false : rand() < 0.3;
      act = talking ? 'talk' : 'listen';
      actDur = 2.5 + rand() * 3;
      actWait = 0.2 + rand() * 0.8;
    } else if (exert > 0.6) {
      act = 'winded';
      actDur = 1.8 + exert * 1.4;
      actWait = 1 + rand() * 2;
    } else {
      const r = rand();
      const old = traits.age > 0.6;
      if (traits.smoker && r < 0.35) {
        act = 'smoke';
        actDur = 5.2 + rand() * 2.6;
      } else if (r < 0.46) {
        act = 'watch';
        actDur = 1.8 + rand() * 0.8;
      } else if (r < 0.64) {
        act = old && !traits.female ? 'behind' : traits.female ? 'fold' : 'pockets';
        actDur = 4 + rand() * 5;
      } else if (r < 0.76) {
        act = traits.female && rand() < 0.6 ? 'hips' : 'fold';
        actDur = 3.5 + rand() * 4;
      } else if (r < 0.86) {
        // The villain and the lads straighten their coats; old men yawn; the rest scratch their heads.
        act = traits.swagger > 0.4 && rand() < 0.75 ? 'collar' : old && rand() < 0.6 ? 'yawn' : traits.female ? 'hips' : 'scratch';
        actDur = act === 'collar' ? 3.4 + rand() * 0.8 : act === 'yawn' ? 2.6 + rand() * 0.8 : act === 'hips' ? 3 + rand() * 3 : 1.3 + rand() * 0.6;
      } else {
        act = old ? 'yawn' : rand() < 0.5 ? 'scratch' : traits.swagger > 0.4 ? 'collar' : 'watch';
        actDur = act === 'yawn' ? 2.6 + rand() * 0.8 : act === 'collar' ? 3.4 + rand() * 0.8 : act === 'scratch' ? 1.3 + rand() * 0.6 : 1.8 + rand() * 0.8;
      }
      actWait = 3 + rand() * 6;
    }
    actT = 0;
    shownAct = act;
  }

  function evalTurn(p: Pose): void {
    evalIdle(p);
    const dir = turnS >= 0 ? 1 : -1;
    const mag = clamp01(Math.abs(turnS) / 3);
    // Small pivot steps on the spot.
    const sn = Math.sin(TAU * turnPhase);
    const liftL = Math.max(0, sn) * 0.35 * mag;
    const liftR = Math.max(0, -sn) * 0.35 * mag;
    add(p, J.upperLegL, 0, -liftL * 0.6);
    add(p, J.lowerLegL, 0, liftL * 1.2);
    add(p, J.footL, 0, -liftL * 0.6);
    add(p, J.upperLegR, 0, -liftR * 0.6);
    add(p, J.lowerLegR, 0, liftR * 1.2);
    add(p, J.footR, 0, -liftR * 0.6);
    p[POSE_HIPS_OFFSET + 1] = p[POSE_HIPS_OFFSET + 1]! - 0.02 * mag * h;
    // Upper body leads, pelvis follows, slight lean into the turn.
    add(p, J.hips, 1, dir * 0.08 * mag);
    add(p, J.chest, 1, dir * 0.2 * mag);
    add(p, J.head, 1, dir * 0.3 * mag);
    add(p, J.spine, 2, -dir * 0.04 * mag);
  }

  function evalStop(p: Pose, t: number): void {
    evalIdle(p);
    const k = (1 - smooth(t / STOP_TIME)) * stopIntensity;
    add(p, J.spine, 0, -0.14 * k);
    add(p, J.head, 0, 0.1 * k);
    add(p, J.upperLegL, 0, -0.35 * k);
    add(p, J.lowerLegL, 0, 0.12 * k);
    add(p, J.footL, 0, 0.23 * k);
    add(p, J.upperLegR, 0, 0.12 * k);
    add(p, J.lowerLegR, 0, 0.25 * k);
    add(p, J.upperArmL, 0, -0.25 * k);
    add(p, J.upperArmR, 0, -0.25 * k);
    p[POSE_HIPS_OFFSET + 1] = p[POSE_HIPS_OFFSET + 1]! - 0.05 * k * h;
  }

  /** Approach for vehicle actions: idle while standing, locomotion when walking to the door. */
  function evalApproach(p: Pose): void {
    const m = clamp01(evalSpeed / 1.2);
    if (m <= 0) {
      evalIdle(p);
      return;
    }
    evalLoco(p, evalSpeed);
    if (m < 1) {
      evalIdle(scratch);
      lerpPose(p, scratch, p, m);
    }
  }

  function duck(p: Pose, w: number): void {
    if (w <= 0) return;
    add(p, J.spine, 0, 0.55 * w);
    add(p, J.chest, 0, 0.15 * w);
    add(p, J.head, 0, -0.25 * w);
    add(p, J.upperLegL, 0, -0.9 * w);
    add(p, J.upperLegR, 0, -0.9 * w);
    add(p, J.lowerLegL, 0, 1.0 * w);
    add(p, J.lowerLegR, 0, 1.0 * w);
    add(p, J.footL, 0, -0.1 * w);
    add(p, J.footR, 0, -0.1 * w);
    add(p, J.upperArmL, 0, -0.4 * w);
    add(p, J.upperArmR, 0, -0.4 * w);
    p[POSE_HIPS_OFFSET + 1] = p[POSE_HIPS_OFFSET + 1]! - 0.28 * h * w;
  }

  function evalEnter(p: Pose, t: number): void {
    evalApproach(p);
    // Reach for the handle, swing the door open, duck in.
    const reach = band(0.3, 0.5, t) * (1 - band(0.62, 0.72, t));
    toward(p, J.upperArmR, 0, -1.15, reach);
    toward(p, J.upperArmR, 2, -0.25, reach);
    toward(p, J.lowerArmR, 0, -0.25, reach);
    const pull = band(0.55, 0.68, t) * (1 - band(0.82, 0.9, t));
    toward(p, J.upperArmR, 0, -0.7, pull);
    toward(p, J.upperArmR, 2, -0.9, pull);
    toward(p, J.lowerArmR, 0, -0.3, pull);
    add(p, J.chest, 1, -0.35 * pull);
    add(p, J.spine, 0, -0.06 * pull);
    const d = band(0.78, 1, t);
    duck(p, d);
    // Left hand on the roof, right leg lifted in over the sill, head down under the door frame.
    const roof = band(0.64, 0.8, t);
    toward(p, J.upperArmL, 0, -1.9, roof * 0.8);
    toward(p, J.upperArmL, 2, 0.35, roof * 0.8);
    toward(p, J.lowerArmL, 0, -0.5, roof * 0.8);
    const legIn = band(0.82, 0.95, t);
    add(p, J.upperLegR, 0, -0.55 * legIn);
    add(p, J.lowerLegR, 0, 0.5 * legIn);
    add(p, J.upperLegR, 2, 0.25 * legIn);
    add(p, J.head, 0, 0.2 * d);
  }

  function evalSteal(p: Pose, t: number): void {
    evalApproach(p);
    // Yank the door with rapid two-handed tugs, haul the driver out with a big torso sweep, duck in.
    const yank = band(0.25, 0.35, t) * (1 - band(0.55, 0.62, t));
    const tug = 0.5 + 0.5 * Math.sin(t * 55);
    toward(p, J.upperArmL, 0, -1.25 + 0.25 * tug, yank);
    toward(p, J.lowerArmL, 0, -0.35 - 0.25 * tug, yank);
    toward(p, J.upperArmR, 0, -1.25 + 0.25 * tug, yank);
    toward(p, J.lowerArmR, 0, -0.35 - 0.25 * tug, yank);
    add(p, J.spine, 0, (-0.15 - 0.1 * tug) * yank);
    add(p, J.upperLegL, 0, -0.3 * yank);
    add(p, J.lowerLegL, 0, 0.3 * yank);
    add(p, J.upperLegR, 0, 0.25 * yank);
    p[POSE_HIPS_OFFSET + 2] = p[POSE_HIPS_OFFSET + 2]! - 0.04 * tug * yank * h;
    const pull = band(0.55, 0.65, t) * (1 - band(0.78, 0.86, t));
    const sweep = band(0.58, 0.8, t);
    add(p, J.chest, 1, (0.3 - 1.1 * sweep) * pull);
    add(p, J.spine, 1, (0.1 - 0.4 * sweep) * pull);
    add(p, J.spine, 0, -0.1 * pull);
    toward(p, J.upperArmL, 0, -1.2 + 0.9 * sweep, pull);
    toward(p, J.upperArmR, 0, -1.2 + 0.9 * sweep, pull);
    toward(p, J.upperArmR, 2, -0.2 - 0.5 * sweep, pull);
    p[POSE_HIPS_OFFSET + 2] = p[POSE_HIPS_OFFSET + 2]! - 0.08 * sweep * pull * h;
    // Leg braced against the sill while hauling; then a shove to send the driver sprawling.
    add(p, J.upperLegL, 0, -0.35 * pull);
    add(p, J.lowerLegL, 0, 0.15 * pull);
    const shove = band(0.68, 0.8, t) * (1 - band(0.82, 0.96, t));
    toward(p, J.upperArmL, 0, -1.45, shove);
    toward(p, J.upperArmR, 0, -1.45, shove);
    toward(p, J.lowerArmL, 0, -0.1, shove);
    toward(p, J.lowerArmR, 0, -0.1, shove);
    add(p, J.spine, 0, 0.15 * shove);
    duck(p, band(0.82, 1, t));
  }

  function evalExit(p: Pose, t: number): void {
    evalApproach(p);
    duck(p, 1 - band(0, 0.3, t));
    // Right leg out first, a hand on the door frame to haul up.
    const legOut = 1 - band(0.05, 0.28, t);
    add(p, J.upperLegR, 0, -0.5 * legOut);
    add(p, J.lowerLegR, 0, 0.35 * legOut);
    const frame = band(0.02, 0.18, t) * (1 - band(0.24, 0.46, t));
    toward(p, J.upperArmR, 0, -1.6, frame);
    toward(p, J.upperArmR, 2, -0.3, frame);
    toward(p, J.lowerArmR, 0, -0.6, frame);
    // Shove the door shut with the left hand.
    const push = band(0.25, 0.45, t) * (1 - band(0.7, 0.9, t));
    toward(p, J.upperArmL, 0, -1.0 + 0.4 * band(0.45, 0.7, t), push);
    toward(p, J.upperArmL, 2, 0.6, push);
    toward(p, J.lowerArmL, 0, -0.2, push);
    add(p, J.chest, 1, 0.25 * push);
  }

  function evalKnockdown(p: Pose, t: number): void {
    if (t < 0.18) {
      lerpPose(p, STAND, AIRBORNE, smooth(t / 0.18));
    } else if (t < 0.45) {
      const s = (t - 0.18) / 0.27;
      lerpPose(p, AIRBORNE, LYING, s * s);
      // Arms flail in the air.
      const flail = (1 - s) * 0.35;
      add(p, J.upperArmL, 0, flail * Math.sin(t * 31));
      add(p, J.upperArmR, 0, flail * Math.sin(t * 27 + 1));
      add(p, J.lowerLegL, 0, flail * 0.6 * Math.sin(t * 23));
    } else {
      p.set(LYING);
      const tt = t - 0.45;
      // Thud: a small bounce on impact (arms splay), then winded breathing, the head lolls and lifts.
      p[POSE_HIPS_OFFSET + 1] = p[POSE_HIPS_OFFSET + 1]! + 0.035 * h * Math.exp(-tt * 9) * Math.sin(tt * 25);
      const splay = Math.exp(-tt * 6);
      add(p, J.upperArmL, 2, 0.2 * splay);
      add(p, J.upperArmR, 2, -0.2 * splay);
      add(p, J.chest, 0, 0.03 * Math.sin((TAU * clock) / 1.6) * smooth(tt / 0.5));
      const groan = band(0.5, 0.9, tt) * (1 - band(1.4, 1.9, tt));
      add(p, J.head, 0, 0.25 * groan);
      add(p, J.head, 1, 0.2 * groan * Math.sin(tt * 2));
      add(p, J.lowerLegR, 0, 0.35 * groan);
      add(p, J.upperLegR, 0, -0.25 * groan);
    }
  }

  function evalGetUp(p: Pose, t: number): void {
    if (t < 0.4) {
      lerpPose(p, LYING, SIT, smooth(t / 0.4));
      // Push up off the ground with both hands behind.
      const push = band(0.08, 0.24, t) * (1 - band(0.26, 0.38, t));
      add(p, J.upperArmL, 0, 0.6 * push);
      add(p, J.upperArmR, 0, 0.6 * push);
    } else if (t < 0.75) {
      lerpPose(p, SIT, KNEEL_UP, smooth((t - 0.4) / 0.35));
      // A shake of the head.
      add(p, J.head, 1, 0.25 * Math.sin((t - 0.4) * 30) * band(0.45, 0.55, t) * (1 - band(0.65, 0.75, t)));
    } else {
      const s = smooth((t - 0.75) / (GETUP_TIME - 0.75));
      lerpPose(p, KNEEL_UP, STAND, s);
      // Right hand pushing on the right knee to stand, a wobble at the top.
      const k = (1 - s) * band(0.75, 0.92, t);
      toward(p, J.upperArmR, 0, -0.75, k);
      toward(p, J.lowerArmR, 0, -0.55, k);
      add(p, J.spine, 2, 0.06 * Math.sin((t - 0.75) * 18) * s * (1 - s) * 4);
    }
  }

  function evalBusted(p: Pose, t: number): void {
    if (t < 0.35) lerpPose(p, STAND, HANDS_UP, smooth(t / 0.35));
    else if (t < 0.55) p.set(HANDS_UP);
    else lerpPose(p, HANDS_UP, KNEEL_SURRENDER, smooth((t - 0.55) / 0.6));
    // A start (shoulders jump) as the shout comes, then a nervous tremble and heavy breathing; once
    // on the knees the head drops and glances about.
    const start = band(0, 0.08, t) * (1 - band(0.12, 0.3, t));
    add(p, J.shoulderL, 2, 0.18 * start);
    add(p, J.shoulderR, 2, -0.18 * start);
    add(p, J.head, 0, -0.15 * start);
    const tremble = 0.025 * Math.sin(clock * 31) * smooth(t / 0.35);
    add(p, J.upperArmL, 2, tremble);
    add(p, J.upperArmR, 2, -tremble);
    add(p, J.chest, 0, 0.02 * Math.sin((TAU * clock) / 1.4));
    const kneel = band(0.8, 1.3, t);
    add(p, J.head, 0, 0.22 * kneel);
    add(p, J.head, 1, 0.25 * kneel * Math.sin(clock * 0.9));
  }

  function evalCower(p: Pose, t: number): void {
    // Crouched low, back to the danger, forearms wrapped over the head; trembling.
    const k = smooth(t / 0.25);
    evalIdle(scratch);
    lerpPose(p, scratch, COWER, k);
    const tremble = 0.03 * Math.sin(clock * 27) * k;
    add(p, J.upperArmL, 2, tremble);
    add(p, J.upperArmR, 2, -tremble);
    add(p, J.chest, 0, 0.025 * Math.sin((TAU * clock) / 0.9) * k);
  }

  function evalWasted(p: Pose, t: number): void {
    // Doubled over, a stagger, down on the knees, then over onto the face.
    if (t < 0.3) {
      lerpPose(p, STAND, BUCKLE, smooth(t / 0.3));
      add(p, J.spine, 2, 0.12 * Math.sin(t * 12) * smooth(t / 0.3));
    } else if (t < 0.62) lerpPose(p, BUCKLE, KNEEL_SLUMP, smooth((t - 0.3) / 0.32));
    else if (t < 1.02) {
      const s = (t - 0.62) / 0.4;
      lerpPose(p, KNEEL_SLUMP, FACE_DOWN, s * s);
    } else p.set(FACE_DOWN);
  }

  function evaluate(s: AnimState, t: number, p: Pose): void {
    switch (s) {
      case 'idle':
        evalIdle(p);
        break;
      case 'walk':
      case 'run':
      case 'sprint':
        evalLoco(p, evalSpeed);
        break;
      case 'start':
        evalLoco(p, evalSpeed);
        add(p, J.spine, 0, 0.12 * (1 - smooth(t / START_TIME)));
        break;
      case 'stop':
        evalStop(p, t);
        break;
      case 'turn':
        evalTurn(p);
        break;
      case 'enterVehicle':
        evalEnter(p, evalProgress);
        break;
      case 'stealVehicle':
        evalSteal(p, evalProgress);
        break;
      case 'exitVehicle':
        evalExit(p, evalProgress);
        break;
      case 'knockdown':
        evalKnockdown(p, t);
        break;
      case 'getUp':
        evalGetUp(p, t);
        break;
      case 'busted':
        evalBusted(p, t);
        break;
      case 'wasted':
        evalWasted(p, t);
        break;
      case 'cower':
        evalCower(p, t);
        break;
    }
  }

  // --- transitions --------------------------------------------------------------------------------
  function desired(input: AnimInput): AnimState {
    if (state === 'wasted') return 'wasted';
    switch (input.action) {
      case 'wasted':
      case 'busted':
      case 'knockdown':
      case 'enterVehicle':
      case 'exitVehicle':
      case 'stealVehicle':
      case 'cower':
        return input.action;
      default:
        break;
    }
    if (state === 'knockdown') return 'getUp';
    if (state === 'getUp' && stateTime < GETUP_TIME) return 'getUp';
    const loco = LOCO.has(state);
    const moving = inSpeed > (loco ? 0.15 : 0.35);
    if (!moving) {
      if (loco) return 'stop';
      if (state === 'stop' && stateTime < STOP_TIME) return 'stop';
      if (Math.abs(inTurn) > (state === 'turn' ? 0.5 : 1.0)) return 'turn';
      return 'idle';
    }
    if (state === 'idle' || state === 'turn' || state === 'stop' || state === 'getUp' || state === 'busted' || state === 'cower') return 'start';
    if (state === 'start' && stateTime < START_TIME) return 'start';
    return gaitFor(inSpeed, inRunning, state);
  }

  function transition(next: AnimState): void {
    if (weights.to < 1) {
      fromPose.set(base);
      fromSnapshot = true;
    } else {
      fromState = state;
      fromTime = stateTime;
      fromSnapshot = false;
      fromSpeed = lastSpeed;
      fromProgress = lastProgress;
    }
    if (next === 'stop') stopIntensity = 0.25 + 0.75 * clamp01(speedS / 6.6);
    previousState = state;
    state = next;
    stateTime = 0;
    blendT = 0;
    // Gait to gait (walk ↔ run ↔ sprint) cross-fades over the longer GAIT_BLEND_TIME: the stride is
    // continuous in speed, so the longer fade only softens the arm swing, lean and sprint weight.
    blendDur = GAITS.has(previousState) && GAITS.has(next) ? GAIT_BLEND_TIME : BLEND_TIME[next];
    weights.from = 1;
    weights.to = 0;
  }

  function emitStep(left: boolean, heading: number, intensity: number): void {
    stepsThisFrame++;
    if (!api.onFootstep) return;
    const a = stanceAmplitude(inSpeed);
    const lx = (left ? 1 : -1) * dims.hipHalf * visualScale;
    const lz = a * visualScale;
    const c = Math.cos(heading);
    const s = Math.sin(heading);
    step.foot = left ? 'left' : 'right';
    step.dx = lx * c + lz * s;
    step.dz = -lx * s + lz * c;
    step.speed = inSpeed;
    step.intensity = intensity;
    api.onFootstep(step);
  }

  function update(input: AnimInput): Pose {
    const dt = input.dt > 0 ? Math.min(input.dt, 0.1) : 0;
    inSpeed = input.speed > 0 ? input.speed : 0;
    inRunning = input.running;
    inTurn = Number.isFinite(input.turnRate) ? input.turnRate : 0;
    inProgress = clamp01(input.actionProgress || 0);
    clock += dt;
    stepsThisFrame = 0;

    // Smoothed drivers.
    if (dt > 0) {
      accelS += (clamp((inSpeed - lastSpeed) / dt, -14, 14) - accelS) * damp(6, dt);
      speedS += (inSpeed - speedS) * damp(10, dt);
    }
    turnS += (inTurn - turnS) * damp(8, dt);
    turnPhase = (turnPhase + (Math.abs(inTurn) * dt) / 2.4) % 1;

    // Idle variation timers.
    shiftTimer -= dt;
    if (shiftTimer <= 0) {
      shiftTarget = shiftTarget > 0 ? -(0.5 + rand() * 0.5) : 0.5 + rand() * 0.5;
      if (rand() < 0.25) shiftTarget = 0;
      shiftTimer = 3.5 + rand() * 3.5;
    }
    shift += (shiftTarget - shift) * damp(2.2, dt);
    lookTimer -= dt;
    if (lookTimer <= 0) {
      lookTarget = rand() < 0.45 ? 0 : rand() * 2 - 1;
      lookTimer = 2 + rand() * 3;
    }
    look += (lookTarget - look) * damp(3, dt);

    // Distance-driven locomotion phase and footsteps.
    const prev = phase;
    const dPhase = Math.min((inSpeed * dt) / (cycleLength(inSpeed, h) * strideK()), 0.49);
    phase = (phase + dPhase) % 1;
    const next = desired(input);
    if (next !== state) transition(next);
    lastSpeed = inSpeed;
    lastProgress = inProgress;
    if (dPhase > 0 && inSpeed > 0.3 && FOOTED.has(state)) {
      const intensity = gaitAmount(inSpeed);
      const heading = input.heading ?? 0;
      if (phase < prev) emitStep(true, heading, intensity);
      else if (prev < 0.5 && phase >= 0.5) emitStep(false, heading, intensity);
    }

    stateTime += dt;
    fromTime += dt;
    blendT += dt;
    sprintW += ((state === 'sprint' ? 1 : 0) - sprintW) * damp(5, dt);
    locoW += ((LOCO.has(state) || state === 'stop' ? 1 : 0) - locoW) * damp(6, dt);

    // Exertion and idle habits.
    const exertRate = state === 'sprint' ? 0.16 : state === 'run' ? 0.06 : LOCO.has(state) ? -0.02 : -0.06;
    exert = clamp01(exert + exertRate * dt);
    social += (clamp01(input.social ?? 0) - social) * damp(2, dt);
    const idleNow = state === 'idle' && stateTime > 0.6 && !(input.weapon && input.weapon.hold !== 'none' && (input.weapon.raise > 0.05 || input.weapon.move !== 'none'));
    if (idleNow) {
      if (act === 'none') {
        actWait -= dt * (social > 0.5 || exert > 0.6 ? 4 : 1);
        if (actWait <= 0) nextAct();
      } else {
        actT += dt;
        if (actT >= actDur) {
          act = 'none';
          if (social > 0.5) actWait = 0.3;
        }
      }
    } else if (act !== 'none') {
      act = 'none';
      actWait = 1.5;
    }
    actW += ((act !== 'none' ? 1 : 0) - actW) * damp(5, dt);
    if (act !== 'none') actT = Math.min(actT, actDur);
    else if (actW > 1e-3) actT += dt;

    // Evaluate and cross-fade.
    evalSpeed = inSpeed;
    evalProgress = inProgress;
    evaluate(state, stateTime, toPose);
    const w = blendDur > 0 ? smooth(blendT / blendDur) : 1;
    weights.to = w;
    weights.from = 1 - w;
    if (w < 1) {
      if (!fromSnapshot) {
        evalSpeed = fromSpeed;
        evalProgress = fromProgress;
        evaluate(fromState, fromTime, fromPose);
      }
      lerpPose(base, fromPose, toPose, w);
    } else {
      base.set(toPose);
    }
    out.set(base);

    // Layers: lead and lean into turns while moving.
    if (locoW > 1e-3) {
      const lead = clamp(turnS * 0.07, -0.3, 0.3) * locoW;
      add(out, J.chest, 1, lead);
      add(out, J.head, 1, lead * 1.4);
      const bank = clamp(-turnS * inSpeed * 0.012, -0.16, 0.16) * locoW;
      add(out, J.hips, 2, bank);
      add(out, J.spine, 2, bank * 0.5);
    }

    // Weapon layer: carry pose, aim, swing, recoil, reload (upper body only; the legs keep their gait).
    const wpn = input.weapon;
    const hold: WeaponHold = wpn ? wpn.hold : 'none';
    if (hold !== prevHold) {
      // Drawn from the coat when a weapon comes out (not when switching to bare fists).
      drawT = hold !== 'none' && hold !== 'fist' ? 0 : 1;
      prevHold = hold;
    }
    drawT = Math.min(1, drawT + dt / 0.38);
    if (wpn && wpn.move === 'swing') {
      if (prevMove !== 'swing' || wpn.t < prevT - 0.3) {
        combo = clock - lastSwingEnd < 0.7 ? combo + 1 : 0;
      }
      prevT = wpn.t;
    } else if (prevMove === 'swing') lastSwingEnd = clock;
    prevMove = wpn ? wpn.move : 'none';
    if (wpn && wpn.hold !== 'none' && !NO_WEAPON_LAYER.has(state)) {
      wctx.combo = combo;
      wctx.still = 1 - clamp01(speedS / 1.2);
      wctx.draw = drawT;
      applyWeaponLayer(out, wpn, h, wctx);
    }

    // Hit reaction: a flinch, or a stagger away from the blow.
    const hit = input.hit;
    if (hit && hit.t >= 0 && hit.t < 1 && !NO_REACT.has(state)) applyHit(out, hit);

    // Head look-at (eased; the chest helps past ~45°), not while lying down or climbing in.
    const lw = input.lookWeight ?? 0;
    const target = lw > 0 && Number.isFinite(input.lookYaw ?? NaN) && !NO_LOOK.has(state) ? clamp(wrap(input.lookYaw!), -1.25, 1.25) * clamp01(lw) : 0;
    lookS += (target - lookS) * damp(5, dt);
    if (Math.abs(lookS) > 1e-3 && !NO_LOOK.has(state)) {
      const extra = Math.sign(lookS) * Math.max(0, Math.abs(lookS) - 0.75);
      add(out, J.neck, 1, (lookS - extra) * 0.35);
      add(out, J.head, 1, (lookS - extra) * 0.65 - out[J.head * 3 + 1]! * 0.5 * clamp01(lw));
      add(out, J.chest, 1, extra * 0.8);
    }

    // Coat tail: damped spring towards a target driven by speed, stride, inertia and turning.
    const thighs = (out[J.upperLegL * 3]! + out[J.upperLegR * 3]!) * 0.5;
    const targetX = clamp(0.12 * clamp01(inSpeed / 6) + 0.035 * clamp(accelS, -6, 6) + Math.min(0, thighs) * 0.7, -1.2, 0.5);
    const targetZ = clamp(-out[J.hips * 3 + 2]! * 0.8 - turnS * inSpeed * 0.012, -0.35, 0.35);
    let remaining = dt;
    while (remaining > 1e-6) {
      const sdt = Math.min(remaining, 1 / 60);
      remaining -= sdt;
      coatVX += (140 * (targetX - coatX) - 9 * coatVX) * sdt;
      coatX += coatVX * sdt;
      coatVZ += (110 * (targetZ - coatZ) - 8 * coatVZ) * sdt;
      coatZ += coatVZ * sdt;
    }
    out[J.coatTail * 3] = coatX;
    out[J.coatTail * 3 + 2] = coatZ;
    return out;
  }

  /** Flinch / stagger layer. `dir` = where the blow came from (body-relative, 0 = in front). */
  function applyHit(p: Pose, r: HitReaction): void {
    const s = clamp01(r.strength);
    const t = r.t;
    const k = (t < 0.07 ? t / 0.07 : Math.exp(-(t - 0.07) * (s > 0.5 ? 4 : 7))) * (0.5 + 0.5 * s);
    if (k < 1e-3) return;
    const fx = Math.sin(r.dir); // + = the blow came from the left
    const fz = Math.cos(r.dir); // + = from the front
    // Head snaps away, shoulders hunch, chest turns from the blow, hands half up.
    add(p, J.head, 0, -0.35 * fz * k);
    add(p, J.head, 2, -0.3 * fx * k);
    add(p, J.neck, 0, -0.12 * fz * k);
    add(p, J.shoulderL, 2, 0.14 * k);
    add(p, J.shoulderR, 2, -0.14 * k);
    add(p, J.chest, 1, -0.3 * fx * k);
    add(p, J.chest, 0, -0.12 * fz * k);
    toward(p, J.upperArmL, 0, -0.65, k * 0.6);
    toward(p, J.upperArmR, 0, -0.65, k * 0.6);
    toward(p, J.lowerArmL, 0, -1.5, k * 0.6);
    toward(p, J.lowerArmR, 0, -1.5, k * 0.6);
    if (s > 0.5) {
      // Stagger: the body rocks away and a foot steps back to catch it.
      const st = (s - 0.5) * 2 * k;
      add(p, J.spine, 0, -0.32 * fz * st);
      add(p, J.spine, 2, 0.28 * fx * st);
      add(p, J.hips, 2, 0.12 * fx * st);
      const back = fz >= 0 ? J.upperLegR : J.upperLegL;
      add(p, back, 0, 0.45 * fz * st);
      add(p, back === J.upperLegR ? J.lowerLegR : J.lowerLegL, 0, 0.35 * st);
      p[POSE_HIPS_OFFSET] = p[POSE_HIPS_OFFSET]! - 0.07 * fx * st * h;
      p[POSE_HIPS_OFFSET + 1] = p[POSE_HIPS_OFFSET + 1]! - 0.04 * st * h;
      p[POSE_HIPS_OFFSET + 2] = p[POSE_HIPS_OFFSET + 2]! - 0.08 * fz * st * h;
      add(p, J.upperArmL, 2, 0.5 * st);
      add(p, J.upperArmR, 2, -0.5 * st);
    }
  }

  function reset(to: AnimState = 'idle'): void {
    act = 'none';
    actW = 0;
    actWait = 2;
    lookS = 0;
    exert = 0;
    drawT = 1;
    combo = 0;
    prevMove = 'none';
    state = to;
    previousState = to;
    fromState = to;
    stateTime = 0;
    fromTime = 0;
    fromSnapshot = false;
    blendT = 0;
    blendDur = 0;
    weights.from = 0;
    weights.to = 1;
    phase = 0;
    turnPhase = 0;
    lastSpeed = 0;
    speedS = 0;
    accelS = 0;
    turnS = 0;
    sprintW = 0;
    locoW = 0;
    coatX = coatVX = coatZ = coatVZ = 0;
    evaluate(state, 0, base);
    out.set(base);
  }

  const api: CharacterAnimator = {
    skeleton,
    get state() {
      return state;
    },
    get previousState() {
      return previousState;
    },
    get stateTime() {
      return stateTime;
    },
    get phase() {
      return phase;
    },
    weights,
    pose: out,
    get stepsThisFrame() {
      return stepsThisFrame;
    },
    onFootstep: null,
    get idleAct() {
      return actW > 0.05 ? shownAct : 'none';
    },
    traits,
    get combo() {
      return combo;
    },
    setTraits(t) {
      if (t.age !== undefined) traits.age = clamp01(t.age);
      if (t.female !== undefined) traits.female = t.female;
      if (t.smoker !== undefined) traits.smoker = t.smoker;
      if (t.swagger !== undefined) traits.swagger = clamp01(t.swagger);
      if (t.bulk !== undefined) traits.bulk = clamp01(t.bulk);
    },
    update,
    reset,
  };
  reset('idle');
  return api;
}

/** Wrap an angle into (-π, π]. */
function wrap(a: number): number {
  const t = Math.PI * 2;
  a %= t;
  if (a > Math.PI) a -= t;
  else if (a <= -Math.PI) a += t;
  return a;
}
